import { useEffect, useMemo, useState, useRef } from "react";
import { Button, Col, Container, Row, Nav, Tab, TabContent } from "react-bootstrap";
import { ColDef, GridReadyEvent, RowClickedEvent } from "ag-grid-community";
import { DataGrid } from "../../Controls/DataGrid";
import { Plus, Pencil, Trash, Building, CreditCard, Files } from "react-bootstrap-icons";
import { toast } from "react-toastify";
import { ActionDropdown, ActionItem } from "../../Controls/ActionDropdown";
import { PageBar, ToolButton } from "../../Controls/PageBar";
import { useParams } from "react-router-dom";
import { createApiService } from "../../shared/apiConfig";
import { BillingApi, OperatorApi, FacilityApi, ServerRequestsBillingPlanDto, ServerRequestsBillingPlanDetailDto } from "../../shared/api/axios-client";
import { CSSTransition } from "react-transition-group";
import { BillingPlanEditModal } from "./BillingPlanEditModal";
import { FacilityAssignmentModal } from "./FacilityAssignmentModal";
import { ConfirmModal } from "../../Controls/ConfirmModal";
import { Select } from "../../Controls/Select";
import { useNavigate } from "react-router-dom";

interface FacilityBillingInfo {
    facilityId: number;
    facilityName: string;
    operatorName: string;
    billingPlans: Array<{
        billingPlanId: number;
        billingPlanName: string;
        ruleCount?: number;
    }>;
}

export function AdminBillingListPage() {
    const { operatorId } = useParams();
    const [billingPlans, setBillingPlans] = useState<ServerRequestsBillingPlanDto[]>([]);
    const [facilities, setFacilities] = useState<FacilityBillingInfo[]>([]);
    const [showContent, setShowContent] = useState(false);
    const [showPlanModal, setShowPlanModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<ServerRequestsBillingPlanDetailDto | null>(null);
    const [preSelectedPlanId, setPreSelectedPlanId] = useState<number | null>(null);
    const [selectedFacilities, setSelectedFacilities] = useState<number[]>([]);
    const [isCloneMode, setIsCloneMode] = useState(false);
    const [filterPlan, setFilterPlan] = useState<string>("");
    const [singleFacilityMode, setSingleFacilityMode] = useState(false);
    const nodeRef = useRef(null);
    const [activeTab, setActiveTab] = useState<string>("plans");

    useEffect(() => {
        // Nudge ag-Grid to size itself when tab changes
        const id = setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
        return () => clearTimeout(id as any);
    }, [activeTab]);
    const nav = useNavigate();
    
    const billingService = createApiService(BillingApi);
    const operatorService = createApiService(OperatorApi);
    const facilityService = createApiService(FacilityApi);

    // Create dropdown options from billing plans
    const planFilterOptions = useMemo(() => {
        const plans = [
            { value: "", label: "All Plans" },
            { value: "unassigned", label: "Unassigned Facilities" },
            ...billingPlans.map(plan => ({ 
                value: plan.billingPlanId?.toString() || "", 
                label: plan.name || `Plan ${plan.billingPlanId}` 
            }))
        ];
        return plans;
    }, [billingPlans]);

    // Filter facilities based on selected plan
    const filteredFacilities = useMemo(() => {
        if (!filterPlan) return facilities;
        if (filterPlan === "unassigned") {
            return facilities.filter(f => f.billingPlans.length === 0);
        }
        return facilities.filter(f => 
            f.billingPlans.some(plan => plan.billingPlanId.toString() === filterPlan)
        );
    }, [facilities, filterPlan]);

    const columns: ColDef<FacilityBillingInfo>[] = useMemo(() => [
        { 
            field: "facilityName", 
            headerName: "Facility",
            headerTooltip: "Facility Name",
            flex: 2,
            cellRenderer: (params: any) => {
                const data = params.data as FacilityBillingInfo;
                if (!data) return '';
                
                return (
                    <div style={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        justifyContent: 'center',
                        padding: '4px 0'
                    }}>
                        <div style={{ fontWeight: 500, marginBottom: '2px', lineHeight: '1.2' }}>
                            <Building className="me-2" size={16} />
                            {data.facilityName}
                        </div>
                        <div style={{ fontSize: '0.85em', color: '#6c757d', lineHeight: '1.2' }}>
                            {data.operatorName}
                        </div>
                    </div>
                );
            }
        },
        { 
            field: "billingPlans", 
            headerName: "Billing Plans",
            headerTooltip: "Assigned Billing Plans",
            flex: 2,
            cellRenderer: (params: any) => {
                const data = params.data as FacilityBillingInfo;
                if (!data) return '';
                
                return (
                    <div style={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        justifyContent: 'center',
                        padding: '4px 0'
                    }}>
                        {data.billingPlans.length > 0 ? (
                            <>
                                <div style={{ fontWeight: 500, marginBottom: '2px', lineHeight: '1.2' }}>
                                    <CreditCard className="me-2" size={16} />
                                    {data.billingPlans.length === 1 
                                        ? data.billingPlans[0].billingPlanName
                                        : `${data.billingPlans.length} plans assigned`
                                    }
                                </div>
                                <div style={{ fontSize: '0.85em', color: '#6c757d', lineHeight: '1.2' }}>
                                    {data.billingPlans.length === 1 
                                        ? `${data.billingPlans[0].ruleCount || 0} rule${data.billingPlans[0].ruleCount !== 1 ? 's' : ''}`
                                        : data.billingPlans.map(p => p.billingPlanName).join(', ')
                                    }
                                </div>
                            </>
                        ) : (
                            <div style={{ color: '#dc3545', fontStyle: 'italic' }}>
                                No plans assigned
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            headerName: "Actions",
            flex: 1,
            cellRenderer: (params: any) => {
                const data = params.data as FacilityBillingInfo;
                if (!data) return '';
                
                return (
                    <div style={{ 
                        display: 'flex', 
                        gap: '8px', 
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%'
                    }}>
                        <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFacilities([data.facilityId]);
                                setSingleFacilityMode(true);
                                setShowAssignModal(true);
                            }}
                            title="Manage Plans"
                        >
                            {data.billingPlans.length > 0 ? 'Manage Plans' : 'Assign Plans'}
                        </button>
                    </div>
                );
            },
            sortable: false,
            filter: false
        }
    ], []);

    const plansColumns: ColDef<ServerRequestsBillingPlanDto>[] = useMemo(() => [
        { 
            field: "name", 
            headerName: "Plan Name",
            headerTooltip: "Billing Plan Name",
            flex: 2,
            cellRenderer: (params: any) => {
                const data = params.data as ServerRequestsBillingPlanDto;
                if (!data) return '';
                
                return (
                    <div style={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        justifyContent: 'center',
                        padding: '4px 0'
                    }}>
                        <div style={{ fontWeight: 500, marginBottom: '2px', lineHeight: '1.2' }}>
                            {data.name || `Plan ${data.billingPlanId}`}
                        </div>
                        <div style={{ fontSize: '0.85em', color: '#6c757d', lineHeight: '1.2' }}>
                            {data.description || 'No description'}
                        </div>
                    </div>
                );
            }
        },
        { 
            field: "ruleCount", 
            headerName: "Rules",
            headerTooltip: "Number of Billing Rules",
            flex: 1,
            type: "rightAligned",
            valueFormatter: (params) => `${params.value || 0} rule${params.value !== 1 ? 's' : ''}`,
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end' } as any
        },
        { 
            field: "operatorFacilityCount", 
            headerName: "Facilities",
            headerTooltip: "Number of Assigned Facilities",
            flex: 1,
            type: "rightAligned",
            valueFormatter: (params) => `${params.value || 0} facilit${params.value !== 1 ? 'ies' : 'y'}`,
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end' } as any
        },
        {
            headerName: "Actions",
            flex: 1,
            cellRenderer: (params: any) => {
                const data = params.data as ServerRequestsBillingPlanDto;
                if (!data) return '';
                const isAssigned = (data.totalFacilityCount || 0) > 0;

                const actions: ActionItem[] = [
                    {
                        label: "Edit",
                        icon: <Pencil />,
                        onClick: () => handleEditPlan(data.billingPlanId!, false)
                    },
                   
                    {
                        label: "Manage Assignments",
                        icon: <Building />,
                        onClick: () => {
                            setPreSelectedPlanId(data.billingPlanId!);
                            setSelectedFacilities([]);
                            setSingleFacilityMode(false);
                            setShowAssignModal(true);
                        }
                    },
                    {
                        label: "Clone",
                        icon: <Files />,
                        onClick: () => handleEditPlan(data.billingPlanId!, true)
                    },
                    {
                        label: "Delete",
                        icon: <Trash />,
                        variant: 'danger',
                        disabled: isAssigned,
                        onClick: () => {
                            setSelectedPlan(data as any);
                            setShowDeleteModal(true);
                        }
                    }
                ];

                return <ActionDropdown actions={actions} />;
            },
            sortable: false,
            filter: false
        }
    ], []);

    const onGridReady = (params: GridReadyEvent) => {
        params.api.sizeColumnsToFit();
    };

    const handleEditPlan = async (planId: number, clone: boolean) => {
        try {
            const response = await billingService.getBillingPlan({ id: planId });
            setSelectedPlan(response.data?.billingPlan || null);
            setIsCloneMode(clone);
            setShowPlanModal(true);
    } catch (error) {
            toast.error("Error loading plan details.");
        }
    };

    const fetchBillingPlans = async () => {
        try {
            const response = await billingService.getBillingPlans({
                operatorId: operatorId ? parseInt(operatorId) : undefined,
                includeCanceled: false
            });
            setBillingPlans(response.data?.billingPlans || []);
        } catch (error) {
            toast.error("Error loading billing plans.");
        }
    };

    const fetchFacilities = async () => {
        try {
            if (!operatorId) return;
            
            // Get operator facilities and their billing plan assignments in parallel
            const [facilitiesResponse, operatorResponse, billingPlansResponse] = await Promise.all([
                facilityService.listFacilitiesLite({
                    operatorId: parseInt(operatorId)
                }),
                operatorService.get({
                    id: parseInt(operatorId)
                }),
                billingService.getOperatorFacilityBillingPlans({
                    operatorId: parseInt(operatorId)
                })
            ]);
            
            const operatorName = operatorResponse.data?.item?.name || 'Unknown Operator';
            const facilityBillingPlans = billingPlansResponse.data?.billingPlans || [];
            
            // Create a map of facility ID to array of billing plan assignments
            const facilityPlansMap = new Map<number, typeof facilityBillingPlans>();
            facilityBillingPlans.forEach(plan => {
                if (plan.facilityId) {
                    const existing = facilityPlansMap.get(plan.facilityId) || [];
                    facilityPlansMap.set(plan.facilityId, [...existing, plan]);
                }
            });
            
            // Transform facilities data with billing plan assignments
            const facilityData: FacilityBillingInfo[] = (facilitiesResponse.data?.items || []).map(facility => {
                const facilityPlans = facilityPlansMap.get(facility.facilityId!) || [];
                
                // Transform each plan assignment with full plan details
                const transformedPlans = facilityPlans.map(planAssignment => {
                    const planDetails = billingPlans.find(plan => 
                        plan.billingPlanId === planAssignment.billingPlanId
                    );
                    
                    return {
                        billingPlanId: planAssignment.billingPlanId!,
                        billingPlanName: planAssignment.billingPlanName || planDetails?.name || `Plan ${planAssignment.billingPlanId}`,
                        ruleCount: planDetails?.ruleCount
                    };
                });
                
                return {
                    facilityId: facility.facilityId!,
                    facilityName: facility.name || `Facility ${facility.facilityId}`,
                    operatorName,
                    billingPlans: transformedPlans
                };
            });
            
            setFacilities(facilityData);
        } catch (error) {
            toast.error("Error loading facilities.");
        }
    };

    const handleDeletePlan = async () => {
        if (!selectedPlan?.billingPlanId) return;
        
        try {
            await billingService.cancelBillingPlan({ id: selectedPlan.billingPlanId });
            await fetchBillingPlans();
            await fetchFacilities();
        } catch (error) {
            toast.error("Error deleting billing plan.");
        } finally {
            setShowDeleteModal(false);
            setSelectedPlan(null);
        }
    };

    const handlePlanSaved = async () => {
        await fetchBillingPlans();
        await fetchFacilities();
        setShowPlanModal(false);
        setSelectedPlan(null);
        setIsCloneMode(false);
    };

    const handleAssignmentSaved = async () => {
        // Refresh both facilities and billing plans to update facility counts
        await Promise.all([
            fetchFacilities(),
            fetchBillingPlans()
        ]);
        setShowAssignModal(false);
        setSelectedFacilities([]);
        setSingleFacilityMode(false);
        setPreSelectedPlanId(null);
    };

    useEffect(() => {
        const loadData = async () => {
            await fetchBillingPlans();
            setShowContent(true);
        };
        loadData();
    }, [operatorId]);

    useEffect(() => {
        if (billingPlans.length > 0) {
            fetchFacilities();
        }
    }, [billingPlans]);

    if (!operatorId) {
        return <div>Invalid operator ID</div>;
    }

    return (
      
            <Container fluid className="h-100 d-flex flex-column overflow-hidden" ref={nodeRef}>
                <Row className="flex-grow-1" style={{ minHeight: 0 }}>
                    <Col className="h-100 d-flex flex-column" style={{ minHeight: 0 }}>
                        <PageBar title={
                                <div className="d-flex align-items-center gap-3">
                                    <Button variant="link" className="p-0" onClick={() => nav(-1)}> ← Back</Button>
                                    <span>Billing Management</span>
                                </div>
                            }>
                            <ToolButton onClick={() => {
                                setSelectedPlan(null);
                                setIsCloneMode(false);
                                setShowPlanModal(true);
                            }}>
                                <Plus color="maroon" /> Add Billing Plan
                            </ToolButton>
                            <ToolButton onClick={() => {
                                setSelectedFacilities([]);
                                setSingleFacilityMode(false);
                                setPreSelectedPlanId(null);
                                setShowAssignModal(true);
                            }}>
                                <Building color="maroon" /> Assign Plans
                            </ToolButton>
                        </PageBar>

                        <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'plans')} className="mb-3 d-flex">
                            <Nav.Item>
                                <Nav.Link eventKey="plans">Plans</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="facilities">Facilities</Nav.Link>
                            </Nav.Item>
                        </Nav>

                        <div className="d-flex flex-column flex-grow-1 overflow-hidden" style={{ minHeight: 0 }}>
                            {activeTab === 'plans' && (
                                <div className="h-100 d-flex flex-column" style={{ minHeight: 0 }}>
                                    <div className="flex-grow-1" style={{ minHeight: 0 }}>
                                        <DataGrid
                                            rowData={billingPlans}
                                            columnDefs={plansColumns}
                                            onGridReady={onGridReady}
                                            pagination={true}
                                            paginationPageSize={10}
                                            defaultColDef={{
                                                sortable: true,
                                                filter: true,
                                                resizable: true,
                                                cellStyle: {
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }
                                            }}
                                            rowHeight={60}
                                            getRowHeight={() => 60}
                                            tooltipShowDelay={200}
                                        />
                                    </div>
                                </div>
                            )}
                            
                            {activeTab === 'facilities' && (
                                <div className="h-100 d-flex flex-column" style={{ minHeight: 0 }}>
                                    <div className="d-flex justify-content-start mb-2">
                                        <div style={{ width: '260px' }}>
                                            <Select
                                                value={filterPlan}
                                                onChange={(e) => setFilterPlan(e.target.value)}
                                                label="Filter by Plan"
                                                className="mb-0 text-muted"
                                            >
                                                {planFilterOptions.map(option => (
                                                    <option key={option.value || 'empty'} value={option.value || ''}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </Select>
                                        </div>
                                    </div>
                                    <div className="flex-grow-1" style={{ minHeight: 0 }}>
                                        <DataGrid
                                            rowData={filteredFacilities}
                                            columnDefs={columns}
                                            onGridReady={onGridReady}
                                            pagination={true}
                                            paginationPageSize={25}
                                            paginationPageSizeSelector={[10, 25, 50, 100]}
                                            defaultColDef={{
                                                sortable: true,
                                                filter: true,
                                                resizable: true,
                                                cellStyle: {
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }
                                            }}
                                            rowHeight={60}
                                            getRowHeight={() => 60}
                                            tooltipShowDelay={200}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>

                <BillingPlanEditModal
                    show={showPlanModal}
                    setShow={setShowPlanModal}
                    plan={selectedPlan}
                    onSaved={handlePlanSaved}
                    isCloneMode={isCloneMode}
                    operatorId={parseInt(operatorId)}
                />

                <FacilityAssignmentModal
                    show={showAssignModal}
                    setShow={setShowAssignModal}
                    operatorId={parseInt(operatorId)}
                    billingPlans={billingPlans}
                    selectedFacilities={selectedFacilities}
                    onSaved={handleAssignmentSaved}
                    singleFacilityMode={singleFacilityMode}
                    preSelectedPlanId={preSelectedPlanId}
                />

                <ConfirmModal
                    show={showDeleteModal}
                    setShow={setShowDeleteModal}
                    title="Delete Billing Plan"
                    message={`Are you sure you want to delete the billing plan "${selectedPlan?.name}"? This action cannot be undone.`}
                    confirmText="Delete"
                    cancelText="Cancel"
                    onConfirm={handleDeletePlan}
                />
            </Container>
   
    );
}